import { useEffect } from "react";

/**
 * useDocumentMeta — Lightweight SEO helper.
 *
 * Sets document.title and writes meta tags (description, keywords,
 * og:title, og:description, twitter:title, twitter:description) on mount.
 * No external dependency (no react-helmet).
 *
 * On unmount the tags are left in place — most non-JS scrapers won't run
 * route changes anyway, and routing already keeps content cohesive.
 */
function upsertMeta(selector, attrName, attrValue, content) {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function useDocumentMeta({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
    }
    if (keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }
    if (ogTitle || title) {
      upsertMeta(
        'meta[property="og:title"]',
        "property",
        "og:title",
        ogTitle || title
      );
      upsertMeta(
        'meta[name="twitter:title"]',
        "name",
        "twitter:title",
        ogTitle || title
      );
    }
    if (ogDescription || description) {
      upsertMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        ogDescription || description
      );
      upsertMeta(
        'meta[name="twitter:description"]',
        "name",
        "twitter:description",
        ogDescription || description
      );
    }
  }, [title, description, keywords, ogTitle, ogDescription]);
}
